/**
 * The diligence building blocks: panel shell, help popover, score badge,
 * metric lists, the details dialog, risk-adjusted score bars, evidence gaps,
 * valuation and floor-area notices, tenure and EPC labels. Kit components and
 * classes from theme/features/diligence.css only.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { IconAlertTriangle, IconArrowRight, IconArrowUpRight } from '@tabler/icons-react'
import { Alert, Button, Dialog, Info, Popover } from '../../../ui/kit'
import type { AreaPotential, AreaRisk, CardValuationEvidence, FloorAreaAssumption, PotentialAssumption } from '../../../api/areaPotential'
import { floorAreaAssumptionSummary } from '../../../api/floorAreaEvidence'
import { COMPARABLE_BATHROOMS_WARNING, valuationNoticeContent, valuationWarnings, type SingleHomeValuationScope } from '../../../api/valuationPresentation'
import { date, label as titleCase, neutral, num } from './format'
import { areaMetricHelp, type HelpContent } from './help'

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ')

/* ---------- help ---------- */

/** While a popover inside a dialog is open, Escape closes the popover only, not the dialog under it. */
export function useEscapeFirst(open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !event.isComposing) { event.stopPropagation(); close() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, close])
}

/** The information icon; opens what it is / why it matters / how to read it. */
export function Help({ label, help, warning = false }: { label: string; help: HelpContent; warning?: boolean }) {
  const [open, setOpen] = useState(false)
  useEscapeFirst(open, () => setOpen(false))
  const anchor = (
    <button type="button" className={cx('dil-help', warning && 'is-warn')} aria-haspopup="dialog" aria-expanded={open} aria-label={`Explain ${label}`} onClick={() => setOpen(value => !value)}>
      {warning ? <IconAlertTriangle className="ic-sm" aria-hidden="true" /> : <Info text={`Explain ${label}`} />}
    </button>
  )
  return (
    <Popover open={open} onClose={() => setOpen(false)} anchor={anchor} align="start" width={400} className="dil-help-pop">
      <div className="pop-title">{label}</div>
      <div className="dil-help-body">
        <section><div className="pop-label">What it is</div><div>{help.what}</div></section>
        <section><div className="pop-label">Why it matters to you</div><div>{help.why}</div></section>
        <section><div className="pop-label">How to read it</div><div>{help.read}</div></section>
      </div>
    </Popover>
  )
}

/* ---------- scores ---------- */

export type Tone = 'good' | 'avg' | 'poor' | 'kill' | 'na'

/** Colour for a model-supplied band. No thresholds are applied here. */
export function bandTone(band: string | null | undefined): Tone {
  switch (band) {
    case 'excellent': case 'strong': case 'very_strong': case 'low': return 'good'
    case 'good': case 'promising': return 'avg'
    case 'mixed': case 'moderate': case 'limited': case 'marginal': case 'elevated': return 'poor'
    case 'poor': case 'weak': case 'very_weak': case 'severe': return 'kill'
    default: return 'na'
  }
}

/** The saved score with its band colour; the band and score come from the model. */
export function Rating({ score, band, label, compact = false, assumed = false, incomplete }: {
  score: number | null | undefined; band?: string | null; label?: string; compact?: boolean; assumed?: boolean; incomplete?: string
}) {
  const rounded = score == null ? null : Math.round(score)
  const bandLabel = rounded == null ? 'Insufficient evidence' : band ? titleCase(band) : 'Band unavailable'
  const accessible = `${assumed ? 'Assumed / Incomplete evidence. ' : ''}${incomplete ? `${incomplete}. ` : ''}${label ? `${label}: ` : ''}${rounded == null ? 'score unavailable' : `score ${rounded} out of 100`}. ${bandLabel}. Higher is better.`
  return (
    <span className={cx('dil-score', rounded == null ? 'na' : bandTone(band), compact && 'is-compact')} role="img" aria-label={accessible} title={accessible}>
      <b>{rounded ?? '—'}</b>{assumed || incomplete ? <IconAlertTriangle className="ic-sm dil-warn-ic" aria-hidden="true" /> : null}
    </span>
  )
}

/* ---------- panels, groups, sections ---------- */

export function Panel({ title, icon, rating, actions, children, wide, className, label }: {
  title: ReactNode; icon?: ReactNode; rating?: ReactNode; actions?: ReactNode; children: ReactNode; wide?: boolean; className?: string; label?: string
}) {
  return (
    <section className={cx('panel', 'dil-panel', wide && 'is-wide', className)} aria-label={label}>
      <header className="panel-h dil-panel-h">
        <span className="dil-title">{icon}<span className="t-section">{title}</span>{rating}</span>
        {actions ? <span className="dil-actions">{actions}</span> : null}
      </header>
      {children}
    </section>
  )
}

export function Group({ title, action, children, className }: { title: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return <section className={cx('dil-group', className)}><header><h4>{title}</h4>{action ? <span className="dil-actions">{action}</span> : null}</header>{children}</section>
}

export function DetailSection({ title, description, children, className }: { title: string; description?: string; children: ReactNode; className?: string }) {
  return <section className={cx('dil-sec', className)}><header><h4>{title}</h4>{description ? <p>{description}</p> : null}</header>{children}</section>
}

export function Note({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx('dil-note', className)}>{children}</p>
}

/** "Details ↗" opening a wide dialog; the content mounts only while open. */
export function Details({ title, label = 'Details', children }: { title: string; label?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button small onClick={() => setOpen(true)} ariaLabel={`Open ${title.toLowerCase()} details`}>{label}<IconArrowUpRight className="ic-sm" aria-hidden="true" /></Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={title} size="xl" className="dil-dialog">{open ? children : null}</Dialog>
    </>
  )
}

/* ---------- metrics ---------- */

export function Metrics({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cx('dil-metrics', className)}>{children}</dl>
}

export function Metric({ label, value, help }: { label: string; value: ReactNode; help?: HelpContent }) {
  return <div className="dil-metric" data-metric={label}><dt><span>{label}</span>{help ? <Help label={label} help={help} /> : null}</dt><dd>{value}</dd></div>
}

/* ---------- assessment pieces ---------- */

const barWidth = (value: number) => `${Math.max(0, Math.min(100, value))}%`

/** Market support before, and adjusted potential after, the area-risk adjustment. */
export function RiskAdjustedScore({ baseScore, score, risk, label, assumptions = [], assumedWeightPercent }: {
  baseScore: number | null | undefined; score: number | null | undefined; risk: AreaRisk | null | undefined; label: string
  assumptions?: PotentialAssumption[] | null; assumedWeightPercent?: number | null
}) {
  return (
    <div className="dil-risk-adjusted" aria-label={`${label} market support and area risk adjustment`}>
      <div className="dil-bar-row"><span>Market support</span><span className="dil-track" aria-hidden="true">{baseScore != null ? <i style={{ width: barWidth(baseScore) }} /> : null}</span><span className="dil-num">{num(baseScore)}</span></div>
      <div className={cx('dil-bar-row', 'is-adjusted', bandTone(risk?.band))}><span>Adjusted potential</span><span className="dil-track" aria-hidden="true">{score != null ? <i style={{ width: barWidth(score) }} /> : null}</span><span className="dil-num">{num(score)}</span></div>
      {risk?.state === 'partial' ? <Note>Partial area evidence</Note> : null}
      {assumptions?.length ? (
        <div className="dil-note dil-assumed">
          <span>Neutral assumptions{assumedWeightPercent == null ? '' : ` · ${num(assumedWeightPercent)}% of market weighting`}</span>
          <Help warning label={`${label} neutral assumptions`} help={{
            what: <ul className="dil-list">{assumptions.map(item => <li key={item.key}>{titleCase(item.key)}: {new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 }).format(item.defaultValue)} {item.unit.replaceAll('_', ' ')}. {item.reason}</li>)}</ul>,
            why: 'Keeps the saved potential score usable when a required market or area-risk input is unavailable.',
            read: 'These values are declared model assumptions, not observed transactions or prices. Review the missing evidence and score range in Details. Map filter changes do not update this assessment.',
          }} />
        </div>
      ) : null}
    </div>
  )
}

export function AssessmentStatus({ response }: { response: AreaPotential }) {
  return (
    <>
      <div className="dil-status">
        {response.calculation ? <time dateTime={response.calculation.evaluatedAt}>Calculated {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(response.calculation.evaluatedAt))}</time> : <span>Awaiting calculation</span>}
        <span>{response.calculationState === 'ready' ? 'Published assessment' : response.calculationState === 'failed' ? 'Calculation failed' : 'Assessment pending'}</span>
      </div>
      {response.calculationState === 'failed' ? <Alert tight title="Assessment unavailable">No current assessment is available for this property.</Alert> : null}
    </>
  )
}

export function PotentialOutcome({ label, score, band, baseScore, lower, upper, confidence, risk, explanation, help, assumptions, assumedWeightPercent }: {
  label: string; score: number | null | undefined; band: string | null | undefined; baseScore: number | null | undefined
  lower: number | null | undefined; upper: number | null | undefined; confidence: number | null | undefined
  risk: AreaRisk | null | undefined; explanation: string; help: HelpContent
  assumptions?: PotentialAssumption[] | null; assumedWeightPercent?: number | null
}) {
  return (
    <section className={cx('dil-outcome', score == null ? 'na' : bandTone(band))} aria-label={`${label} outcome`}>
      <div className="dil-outcome-head">
        <Rating score={score} band={band} label={`${label} potential`} />
        <div>
          <div className="dil-eyebrow">Adjusted potential · out of 100</div>
          <h3>{score == null ? 'Insufficient evidence' : titleCase(band)}<Help label={`${label} potential`} help={help} /></h3>
          <p>{explanation}</p>
        </div>
      </div>
      <RiskAdjustedScore baseScore={baseScore} score={score} risk={risk} label={label} assumptions={assumptions} assumedWeightPercent={assumedWeightPercent} />
      <div className="dil-coverage">
        <span>Confidence {confidence == null ? 'Unknown' : `${Math.round(confidence * 100)}%`}<Help label="Score confidence" help={areaMetricHelp.confidence} /></span>
        {lower != null && upper != null && lower !== upper ? <span>Range {num(lower)}–{num(upper)}<Help label="Missing-evidence range" help={areaMetricHelp.bounds} /></span> : null}
        <span>Market evidence is assessed before area risk is applied.</span>
      </div>
    </section>
  )
}

export interface Contribution { key: string; label: string; score: number | null; max: number | null; missing: string[] | null; help: HelpContent }

export function ScoreContributions({ items }: { items: Contribution[] }) {
  return (
    <div className="dil-contribs">
      {items.map(item => (
        <div className={cx('dil-contrib', (item.missing?.length ?? 0) > 0 && 'is-missing')} key={item.key}>
          <div><span>{item.label}<Help label={item.label} help={item.help} /></span><span className="dil-num">{num(item.score)}<small> / {num(item.max)}</small></span></div>
          <span className="dil-track" aria-hidden="true">{item.score == null || item.max == null || item.max <= 0 ? null : <i style={{ width: barWidth(item.score / item.max * 100) }} />}</span>
          <small>{item.missing?.length ? `Incomplete · ${item.missing.map(titleCase).join(', ')}` : item.score == null || item.missing === null ? 'Evidence unavailable' : 'Evidence available'}</small>
        </div>
      ))}
    </div>
  )
}

export function EvidenceGaps({ missing, reasons = [], format = titleCase, coverageSummary }: { missing: string[]; reasons?: string[]; format?: (value: string) => string; coverageSummary?: string }) {
  const gaps = [...new Set(missing)]
  const notes = [...new Set(reasons)].filter(reason => !gaps.includes(reason))
  return (
    <section className={cx('dil-gaps', gaps.length > 0 && 'is-incomplete')} aria-label="Evidence review">
      <div><h4>{gaps.length ? 'Missing evidence' : 'Evidence coverage'}</h4>{gaps.length ? <ul className="dil-list">{gaps.map(item => <li key={item}>{format(item)}</li>)}</ul> : <p>{coverageSummary ?? 'No missing inputs reported for this assessment.'}</p>}</div>
      {notes.length ? <div><h4>Worth noting</h4><ul className="dil-list">{notes.map(item => <li key={item}>{format(item)}</li>)}</ul></div> : null}
    </section>
  )
}

/* ---------- valuation and floor-area notices ---------- */

export { NO_SUPPORTED_SUBJECT_GDV } from '../../../api/valuationPresentation'

export function hasComparableBathroomCoverageGap(valuation?: Pick<CardValuationEvidence, 'warnings'> | null): boolean {
  return valuation?.warnings?.includes(COMPARABLE_BATHROOMS_WARNING) === true
}

export function displayableValuationWarnings(valuation?: Pick<CardValuationEvidence, 'warnings' | 'gdvPence'> | null, scope?: SingleHomeValuationScope | null): string[] {
  return valuationWarnings(valuation, scope)
}

/** The notice title the valuation evidence calls for, or null when none applies. */
export function valuationNoticeTitle(valuation?: CardValuationEvidence | null, scope?: SingleHomeValuationScope | null): string | null {
  return valuationNoticeContent(valuation, scope)?.title ?? null
}

export function ValuationNotice({ valuation, scope }: { valuation?: CardValuationEvidence | null; scope?: SingleHomeValuationScope | null }) {
  const notice = valuationNoticeContent(valuation, scope)
  if (!notice) return null
  return (
    <Alert warn tight title={notice.title}>
      {notice.rows.map(warning => <p key={warning}>{warning}</p>)}
    </Alert>
  )
}

export function FloorAreaValue({ value, assumed = false, assumption, unknownLabel = 'Unknown', label }: {
  value: number | null | undefined; assumed?: boolean; assumption?: FloorAreaAssumption | null; unknownLabel?: string; label?: string
}) {
  const warning = `Assumed area. Floor area ${value == null ? 'unknown' : `${num(value)} m²`}. ${neutral(assumption?.reason ?? 'A generic median estimate is used; verify the internal area.')}`
  return (
    <span className="dil-area" role={assumed ? 'img' : undefined} title={assumed ? warning : undefined} aria-label={assumed ? warning : undefined}>
      <span>{label ?? (value == null ? unknownLabel : `${num(value)} m²`)}</span>
      {assumed ? <span className="dil-assumed-tag"><IconAlertTriangle className="ic-sm" aria-hidden="true" />Assumed</span> : null}
    </span>
  )
}

export function FloorAreaAssumptionNotice({ assumed, assumption }: { assumed?: boolean; assumption?: FloorAreaAssumption | null }) {
  if (!assumed) return null
  return (
    <Alert warn tight title="Assumed area">
      <p>{neutral(assumption?.reason ?? 'Floor area uses a generic median estimate. Verify the property’s internal area before relying on area-based costs.')}</p>
      {assumption ? <p>{neutral(floorAreaAssumptionSummary(assumption, value => num(value)))}</p> : null}
    </Alert>
  )
}

/* ---------- tenure and EPC ---------- */

export function tenureLabel(tenure: string | null | undefined, lease: { statedYearsRemaining: number | null; statedAt: string | null } | null | undefined): { text: string; detail: string | null } {
  const plain = titleCase(tenure)
  const years = lease?.statedYearsRemaining
  if (tenure?.trim().toLowerCase() !== 'leasehold' || years === null || years === undefined || !Number.isFinite(years) || years <= 0 || years >= 2000) return { text: plain, detail: null }
  const rounded = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(years)
  const recorded = lease?.statedAt ? ` Source evidence captured or analysed on ${date(lease.statedAt)}.` : ''
  return { text: `Leasehold (${rounded} years)`, detail: `Unexpired lease term stated in a property listing.${recorded} It has not been recalculated to today; verify it against the legal documents.` }
}

export function TenureLabel({ tenure, lease }: { tenure: string | null | undefined; lease: { statedYearsRemaining: number | null; statedAt: string | null } | null | undefined }) {
  const value = tenureLabel(tenure, lease)
  return <span title={value.detail ?? undefined} aria-label={value.detail ? `${value.text}. ${value.detail}` : undefined}>{value.text}{value.detail ? <Info text={value.detail} /> : null}</span>
}

function epcLetter(value: string | null | undefined): string | null {
  const rating = value?.trim().toUpperCase()
  return rating && /^[A-G]$/.test(rating) ? rating : null
}

const EPC_STEP: Record<string, number> = { A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6 }

export function EpcRating({ rating, label }: { rating: string | null | undefined; label?: string }) {
  const letter = epcLetter(rating)
  const style = letter ? ({ '--epc-step': String(EPC_STEP[letter] ?? 0) } as CSSProperties) : undefined
  return <span className={cx('dil-epc', letter && 'is-rated')} style={style} aria-label={label ? `${label}: ${letter ?? 'unknown'}` : undefined}>{letter ?? '—'}</span>
}

export function EpcPair({ current, potential }: { current: string | null | undefined; potential: string | null | undefined }) {
  return (
    <span className="dil-epc-pair" aria-label={`EPC ${epcLetter(current) ?? 'unknown'} to ${epcLetter(potential) ?? 'unknown'}`}>
      <span className="dil-epc-label">EPC</span>
      <EpcRating rating={current} label="Current EPC" />
      <IconArrowRight className="ic-sm" aria-hidden="true" />
      <EpcRating rating={potential} label="Potential EPC" />
    </span>
  )
}

/** Inspection grades (Ofsted and childcare) as tone chips; the grade text is the source's own. */
export function gradeTone(grade: string | null | undefined): Tone | 'info' {
  return grade === 'Urgent improvement' || grade === 'Inadequate' || grade === 'Not met' || grade === 'Not met (enforcement)' ? 'kill'
    : grade === 'Needs attention' || grade === 'Requires improvement' || grade === 'Not met (with actions)' ? 'poor'
      : grade === 'Exceptional' || grade === 'Outstanding' ? 'good'
        : grade === 'Strong standard' || grade === 'Good' ? 'avg'
          : grade === 'Expected standard' || grade === 'Met' ? 'info' : 'na'
}

export function Grade({ grade, children, outline }: { grade?: string | null; children: ReactNode; outline?: boolean }) {
  return <span className={cx('dil-grade', gradeTone(grade), outline && 'is-outline')}>{children}</span>
}
